import type { AnalysisDoc, DealTableSources } from '@/lib/types';
import { SourceChip } from '@/components/smartTable/atoms';

interface Props {
  analysis: AnalysisDoc | null;
}

const ROWS: {
  key: keyof AnalysisDoc['table'];
  label: string;
  /** Maps to the corresponding entry in analysis.tableSources. */
  sourceKey: keyof DealTableSources;
}[] = [
  { key: 'economicBuyer', label: 'Economic Buyer', sourceKey: 'economicBuyer' },
  {
    key: 'economicBuyerSignedOff',
    label: 'EB Signed Off on Use Case',
    sourceKey: 'economicBuyerSignedOff',
  },
  { key: 'useCase', label: 'Use Case', sourceKey: 'useCase' },
  {
    key: 'tentativeLaunchDateAgreed',
    label: 'Tentative Launch Date Agreed',
    sourceKey: 'tentativeLaunchDateAgreed',
  },
  { key: 'budgetApprovedByEB', label: 'Budget Approved by EB', sourceKey: 'budgetApprovedByEB' },
  { key: 'champion', label: 'Champion', sourceKey: 'champion' },
  { key: 'legalContact', label: 'Legal Contact', sourceKey: 'legalContact' },
  { key: 'msaStatus', label: 'Got MSA / Agreed to Terms', sourceKey: 'msaStatus' },
  { key: 'procurementContact', label: 'Procurement Contact', sourceKey: 'procurementContact' },
  { key: 'vendorRegistration', label: 'Registered as Vendor', sourceKey: 'vendorRegistration' },
];

export function DealTableView({ analysis }: Props) {
  if (!analysis) {
    return (
      <div className="card p-5 text-sm text-slate-500">
        No analysis yet. Pull calls or trigger Re-analyze to generate one.
      </div>
    );
  }
  const t = analysis.table;
  const ts = analysis.tableSources;
  return (
    <div className="card overflow-hidden">
      <div className="border-b border-slate-200 px-4 py-3 text-sm font-semibold">
        Deal table
      </div>
      <table className="w-full text-sm">
        <tbody className="divide-y divide-slate-100">
          {ROWS.map((r) => (
            <tr key={r.key}>
              <td className="w-72 bg-slate-50 px-4 py-2 align-top text-xs font-medium uppercase tracking-wide text-slate-500">
                <span className="inline-flex items-center gap-2">
                  <span>{r.label}</span>
                  {ts && <SourceChip kind={ts[r.sourceKey]} />}
                </span>
              </td>
              <td className="px-4 py-2 text-slate-800">
                {(t[r.key] as string | undefined) || (
                  <span className="text-slate-400">Unknown</span>
                )}
              </td>
            </tr>
          ))}
          <tr>
            <td className="w-72 bg-slate-50 px-4 py-2 align-top text-xs font-medium uppercase tracking-wide text-slate-500">
              <span className="inline-flex items-center gap-2">
                <span>Potential blockers</span>
                {ts && <SourceChip kind={ts.potentialBlockers} />}
              </span>
            </td>
            <td className="px-4 py-2 text-slate-800">
              {t.potentialBlockers?.length ? (
                <ul className="list-disc space-y-1 pl-5">
                  {t.potentialBlockers.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              ) : (
                <span className="text-slate-400">None noted</span>
              )}
            </td>
          </tr>
          <tr>
            <td className="w-72 bg-slate-50 px-4 py-2 align-top text-xs font-medium uppercase tracking-wide text-slate-500">
              <span className="inline-flex items-center gap-2">
                <span>Agreed next steps</span>
                {ts && <SourceChip kind={ts.agreedNextSteps} />}
              </span>
            </td>
            <td className="px-4 py-2 text-slate-800">
              {t.agreedNextSteps?.length ? (
                <ul className="list-disc space-y-1 pl-5">
                  {t.agreedNextSteps.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              ) : (
                <span className="text-slate-400">None recorded</span>
              )}
            </td>
          </tr>
          {analysis.redFlags?.length > 0 && (
            <tr>
              <td className="w-72 bg-rose-50 px-4 py-2 align-top text-xs font-medium uppercase tracking-wide text-rose-700">
                Red flags
              </td>
              <td className="px-4 py-2 text-rose-800">
                <ul className="list-disc space-y-1 pl-5">
                  {analysis.redFlags.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="border-t border-slate-200 px-4 py-2 text-xs text-slate-500">
        Generated by {analysis.provider}/{analysis.model} ·{' '}
        {analysis.sourceCallIds?.length ?? 0} source call(s) · prompt v
        {analysis.promptVersion}
      </div>
    </div>
  );
}
